import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { WHATSAPP_URL } from "@/i18n/translations";
import { trackWhatsApp } from "@/lib/analytics";

const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const goToSection = (id) => {
    setOpen(false);
    const onHome = location.pathname === "/";
    if (!onHome) {
      navigate(`/#${id}`);
      // schedule a scroll after the page mounts
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    } else {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  const navItems = [
    ["services", t.nav.services],
    ["cases", t.nav.cases],
    ["pricing", t.nav.pricing],
    ["faq", t.nav.faq],
    ["about", t.nav.about],
  ];

  const langLabel = lang === "en" ? "EN" : lang === "fr" ? "FR" : "AR";

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled || open
            ? "bg-[#0A0A0B]/90 backdrop-blur-xl border-b border-sand-soft"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-16 sm:h-20 flex items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="brand-logo"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 group min-w-0 flex-shrink"
            aria-label="Moroccan AI Factory — Home"
          >
            <span className="font-accent italic text-2xl text-terracotta leading-none flex-shrink-0">M</span>
            <span className="font-display tracking-tight text-maif text-sm sm:text-base whitespace-nowrap">
              <span className="hidden sm:inline">Moroccan </span>
              <span className="text-maif-muted">AI</span> Factory
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7">
            {navItems.map(([id, label]) => (
              <button
                key={id}
                onClick={() => goToSection(id)}
                data-testid={`nav-${id}`}
                className="text-sm text-maif-muted hover:text-maif transition-colors"
              >
                {label}
              </button>
            ))}
            <Link
              to="/blog"
              data-testid="nav-blog"
              className="text-sm text-maif-muted hover:text-maif transition-colors"
            >
              Blog
            </Link>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggle}
              data-testid="language-toggle"
              className="flex h-9 px-3 items-center gap-1 rounded-full border border-sand-soft hover:border-sand-mid text-[11px] sm:text-xs font-semibold text-maif transition"
              aria-label="Toggle language"
            >
              {langLabel}
            </button>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-cta-whatsapp"
              onClick={() => trackWhatsApp("header")}
              className="hidden sm:inline-flex h-9 sm:h-10 px-3 sm:px-5 items-center rounded-full bg-[#D46F4D] hover:bg-[#BD5F3F] text-white text-xs sm:text-sm font-medium transition whitespace-nowrap"
            >
              {t.nav.cta}
            </a>
            {/* Hamburger toggle (mobile only) */}
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
              className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border border-sand-soft text-maif"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-[#0A0A0B]/95 backdrop-blur-xl"
          onClick={() => setOpen(false)}
        />
        <nav className="relative h-full flex flex-col justify-center items-stretch px-8 pt-20">
          <div className="space-y-1">
            {navItems.map(([id, label], i) => (
              <button
                key={id}
                onClick={() => goToSection(id)}
                data-testid={`mobile-nav-${id}`}
                className="block w-full text-left py-5 border-b border-sand-soft font-display text-3xl text-maif tracking-tight hover:text-terracotta transition-colors"
                style={{
                  transitionDelay: open ? `${i * 60}ms` : "0ms",
                  transform: open ? "translateY(0)" : "translateY(20px)",
                  opacity: open ? 1 : 0,
                  transitionProperty: "opacity, transform, color",
                  transitionDuration: "500ms",
                }}
              >
                {label}
              </button>
            ))}
            <Link
              to="/blog"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-blog"
              className="block w-full text-left py-5 border-b border-sand-soft font-display text-3xl text-maif tracking-tight hover:text-terracotta transition-colors"
              style={{
                transitionDelay: open ? `${navItems.length * 60}ms` : "0ms",
                transform: open ? "translateY(0)" : "translateY(20px)",
                opacity: open ? 1 : 0,
                transitionProperty: "opacity, transform, color",
                transitionDuration: "500ms",
              }}
            >
              Blog
            </Link>
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { setOpen(false); trackWhatsApp("mobile_drawer"); }}
            data-testid="mobile-cta-whatsapp"
            className="mt-10 inline-flex justify-center h-14 px-6 items-center rounded-full bg-terracotta text-white font-medium transition"
          >
            {t.nav.cta} →
          </a>
        </nav>
      </div>
    </>
  );
};

export default Header;
